/**
 * MaintenancePanel - Toggle maintenance mode with message and scheduling
 *
 * Features:
 * - Toggle maintenance mode on/off
 * - Set custom maintenance message
 * - Schedule end time
 * - Shows current maintenance status
 * - Confirmation dialog for enabling
 */
import { useState } from 'react';
import { cn } from '@respectly/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import { Button } from '@respectly/ui';
import { Input } from '@respectly/ui';
import { Label } from '@respectly/ui';
import { Switch } from '@respectly/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@respectly/ui';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Construction,
  AlertTriangle,
  Clock,
  User,
  Loader2,
  CheckCircle2,
} from 'lucide-react';
import type { MaintenanceMode, ToggleMaintenanceModeDto } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface MaintenancePanelProps {
  maintenanceMode: MaintenanceMode | null;
  isLoading?: boolean;
  isToggling?: boolean;
  onToggle?: (dto: ToggleMaintenanceModeDto) => Promise<boolean>;
  className?: string;
}

// ============================================
// Helpers
// ============================================

function formatScheduledEnd(date: Date | null | undefined): string {
  if (!date) return 'No end time scheduled';
  return new Date(date).toLocaleString();
}

function formatStartedAt(date: Date | null | undefined): string {
  if (!date) return '';
  const now = new Date();
  const diff = now.getTime() - new Date(date).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 0) return `${hours}h ${minutes}m ago`;
  return `${minutes}m ago`;
}

// ============================================
// Component
// ============================================

export function MaintenancePanel({
  maintenanceMode,
  isLoading = false,
  isToggling = false,
  onToggle,
  className,
}: MaintenancePanelProps) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [message, setMessage] = useState('');
  const [scheduledEnd, setScheduledEnd] = useState('');

  const isActive = maintenanceMode?.isActive ?? false;

  const handleToggle = async () => {
    if (!onToggle) return;

    if (isActive) {
      // Disable maintenance mode
      await onToggle({ isActive: false });
    } else {
      // Show confirmation dialog for enabling
      setShowConfirmDialog(true);
    }
  };

  const handleConfirmEnable = async () => {
    if (!onToggle) return;

    const dto: ToggleMaintenanceModeDto = {
      isActive: true,
      message: message || undefined,
      scheduledEnd: scheduledEnd ? new Date(scheduledEnd) : undefined,
    };

    const success = await onToggle(dto);
    if (success) {
      setShowConfirmDialog(false);
      setMessage('');
      setScheduledEnd('');
    }
  };

  return (
    <>
      <Card
        className={cn(
          'border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900',
          isActive && 'border-amber-300 dark:border-amber-700',
          className
        )}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'h-9 w-9 rounded-lg flex items-center justify-center',
                  isActive
                    ? 'bg-gradient-to-br from-amber-500 to-orange-600'
                    : 'bg-gradient-to-br from-slate-500 to-slate-600'
                )}
              >
                <Construction className="h-4.5 w-4.5 text-white" />
              </div>
              <div>
                <CardTitle className="text-base">Maintenance Mode</CardTitle>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {isActive ? 'Platform is in maintenance' : 'Platform is operational'}
                </p>
              </div>
            </div>

            {/* Toggle Switch */}
            <div className="flex items-center gap-2">
              {isToggling && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              <Switch
                checked={isActive}
                onCheckedChange={handleToggle}
                disabled={isLoading || isToggling}
              />
            </div>
          </div>
        </CardHeader>

        <CardContent className="pt-0">
          <AnimatePresence mode="wait">
            {isLoading ? (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-3 animate-pulse"
              >
                <div className="h-4 w-3/4 bg-slate-200 dark:bg-slate-700 rounded" />
                <div className="h-4 w-1/2 bg-slate-200 dark:bg-slate-700 rounded" />
              </motion.div>
            ) : isActive ? (
              <motion.div
                key="active"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-4"
              >
                {/* Active Status Banner */}
                <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Maintenance Mode Active
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                      Users will see a maintenance message when accessing the platform.
                    </p>
                  </div>
                </div>

                {/* Current Message */}
                {maintenanceMode?.message && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">
                      Message
                    </p>
                    <p className="text-sm text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 rounded-lg p-3">
                      {maintenanceMode.message}
                    </p>
                  </div>
                )}

                {/* Info Row */}
                <div className="flex flex-wrap items-center gap-4 text-xs text-slate-500 dark:text-slate-400">
                  {maintenanceMode?.startedAt && (
                    <div className="flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5" />
                      <span>Started {formatStartedAt(maintenanceMode.startedAt)}</span>
                    </div>
                  )}
                  {maintenanceMode?.scheduledEnd && (
                    <div className="flex items-center gap-1.5">
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                      <span>Ends: {formatScheduledEnd(maintenanceMode.scheduledEnd)}</span>
                    </div>
                  )}
                  {maintenanceMode?.startedBy && (
                    <div className="flex items-center gap-1.5">
                      <User className="h-3.5 w-3.5" />
                      <span>
                        {maintenanceMode.startedBy.firstName} {maintenanceMode.startedBy.lastName}
                      </span>
                    </div>
                  )}
                </div>

                {/* Disable Button */}
                <Button
                  variant="outline"
                  className="w-full border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
                  onClick={handleToggle}
                  disabled={isToggling}
                >
                  {isToggling ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  End Maintenance Mode
                </Button>
              </motion.div>
            ) : (
              <motion.div
                key="inactive"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="space-y-3"
              >
                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <span>Platform is running normally</span>
                </div>
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  Enable maintenance mode to display a maintenance message to all users and
                  prevent new operations.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Construction className="h-5 w-5 text-amber-500" />
              Enable Maintenance Mode
            </DialogTitle>
            <DialogDescription>
              This will display a maintenance message to all users and may affect
              platform functionality.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Message Input */}
            <div className="space-y-2">
              <Label htmlFor="maintenance-message">
                Maintenance Message (optional)
              </Label>
              <Input
                id="maintenance-message"
                placeholder="We're performing scheduled maintenance..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            {/* Scheduled End */}
            <div className="space-y-2">
              <Label htmlFor="scheduled-end">
                Scheduled End Time (optional)
              </Label>
              <Input
                id="scheduled-end"
                type="datetime-local"
                value={scheduledEnd}
                onChange={(e) => setScheduledEnd(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isToggling}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmEnable}
              disabled={isToggling}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isToggling ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Construction className="h-4 w-4 mr-2" />
              )}
              Enable Maintenance Mode
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
